import { useState, useEffect, useCallback } from 'react'
import { getQuestionnaireForCoverType } from './claimsQuestionnaires'
import { supabase } from './supabase'

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const BROKERS = ['Ben Rogers', 'Jesse Roach', 'Mitch Speer']

const CLAIM_STATUSES = [
  'Lodged',
  'Insurer Acknowledged',
  'Under Assessment',
  'Settlement Offered',
  'Settled',
  'Declined',
  'Withdrawn',
]

const CLAIM_STATUS_COLORS = {
  'Lodged':                 { bg: 'rgba(59,130,246,0.1)',   color: '#2563eb' },
  'Insurer Acknowledged':   { bg: 'rgba(139,92,246,0.1)',   color: '#7c3aed' },
  'Under Assessment':       { bg: 'rgba(230,168,23,0.12)',  color: '#7a5500' },
  'Settlement Offered':     { bg: 'rgba(30,61,48,0.1)',     color: '#1e3d30' },
  'Settled':                { bg: 'rgba(30,158,117,0.1)',   color: '#1e9e75' },
  'Declined':               { bg: 'rgba(220,53,69,0.1)',    color: '#dc3545' },
  'Withdrawn':              { bg: 'rgba(107,130,120,0.1)',  color: '#6b8278' },
}

const URGENCY_COLORS = {
  Urgent:   { bg: 'rgba(220,53,69,0.1)',   color: '#dc3545' },
  High:     { bg: 'rgba(230,168,23,0.12)', color: '#7a5500' },
  Standard: { bg: 'rgba(30,61,48,0.08)',   color: '#1e3d30' },
}

const COVER_TYPES = [
  'Truck','Commercial Motor','Car','Plant & Equipment','Commercial Property',
  'Landlord','Public Liability','Professional Indemnity','Management Liability',
  'Carriers','Construction','Farm','Boat','Workers Compensation','PAC',
  'Home & Contents','Caravan','Motorbike','Cyber','Other',
]

const CLAIMS_MADE_TYPES = [
  'Professional Indemnity','Management Liability','Cyber','Directors & Officers',
]

const isClaimsMadeType = (ct) =>
  CLAIMS_MADE_TYPES.some(t => (ct || '').toLowerCase().includes(t.toLowerCase()))

const fmtDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

const fmtCurr = (n) => {
  if (!n || isNaN(n)) return '—'
  return Number(n).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const daysSince = (d) => {
  if (!d) return null
  return Math.floor((new Date() - new Date(d)) / 86400000)
}

// ─── COMPLIANCE BLURB ─────────────────────────────────────────────────────────
function ClaimsComplianceBlurb() {
  return (
    <div style={{
      background: 'linear-gradient(135deg,rgba(220,53,69,0.05),rgba(230,168,23,0.05))',
      border: '1.5px solid rgba(220,53,69,0.2)',
      borderRadius: 14, padding: '16px 20px', marginBottom: 20,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#dc3545', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        ⚖️ Claims Obligations
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        {[
          { title: 'Notify immediately', text: 'Contact the insurer as soon as possible after an incident. Do not delay — late notification can affect cover.' },
          { title: 'Do not admit liability', text: 'Clients must not admit liability to any third party before speaking with us or the insurer.' },
          { title: 'Claims-made policies', text: 'Any claim or circumstance must be notified to the insurer during the current period — not just when the event occurred.' },
          { title: 'Broker advocacy', text: 'We prepare, lodge and manage claims on behalf of the client. We advocate for swift, fair settlement at every stage.' },
          { title: 'Declined claims', text: 'If a claim is declined, request written reasons. Escalate via McLardy McShane, Steadfast Customer Advocacy, or AFCA.' },
          { title: 'Update every 10 days', text: 'Keep clients updated at least every 10 business days. Log every communication in the broker notes.' },
        ].map(item => (
          <div key={item.title} style={{ background: '#fff', borderRadius: 9, padding: '10px 13px', border: '1px solid rgba(220,53,69,0.12)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#dc3545', marginBottom: 3 }}>{item.title}</div>
            <div style={{ fontSize: 11, color: 'var(--text)', lineHeight: 1.6 }}>{item.text}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 8, fontSize: 11, color: '#7c3aed' }}>
        <strong>Escalation path:</strong> Internal review → McLardy McShane complaints officer (tracy@mclardymcshane.com.au) → Steadfast Customer Advocacy (customeradvocacy@steadfast.com.au / 02 9495 6500) → AFCA (1800 931 678)
      </div>
    </div>
  )
}

// ─── CLAIM STATUS BADGE ───────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = CLAIM_STATUS_COLORS[status] || { bg: 'var(--surface2)', color: 'var(--muted)' }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
      borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color,
    }}>{status}</span>
  )
}

// ─── LODGE & MANAGE TAB ───────────────────────────────────────────────────────
function QuestionnaireRenderer({ questionnaire, claimData, onChange }) {
  if (!questionnaire) return null
  const renderField = (field) => {
    const val = claimData[field.key] || ''
    const set = (v) => onChange(prev => ({ ...prev, [field.key]: v }))
    if (field.type === 'textarea') return <textarea className="form-input" rows={3} value={val} onChange={e => set(e.target.value)} />
    if (field.type === 'date') return <input className="form-input" type="date" value={val} onChange={e => set(e.target.value)} />
    if (field.type === 'time') return <input className="form-input" type="time" value={val} onChange={e => set(e.target.value)} />
    if (field.type === 'select') return <select className="form-select" value={val} onChange={e => set(e.target.value)}><option value="">Select...</option>{(field.options||[]).map(o=><option key={o}>{o}</option>)}</select>
    if (field.type === 'radio') return <div style={{display:'flex',gap:10,flexWrap:'wrap',paddingTop:4}}>{(field.options||[]).map(o=><label key={o} style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',fontWeight:val===o?700:400,color:val===o?'var(--forest)':'var(--text)'}}><input type="radio" name={field.key} value={o} checked={val===o} onChange={()=>set(o)} style={{accentColor:'var(--forest)'}}/>{o}</label>)}</div>
    if (field.type === 'checkbox') return <div style={{display:'flex',flexDirection:'column',gap:8,paddingTop:4}}>{(field.options||[]).map(o=>{const checked=Array.isArray(val)?val.includes(o):false;return<label key={o} style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:13,cursor:'pointer'}}><input type="checkbox" checked={checked} onChange={()=>{const c=Array.isArray(val)?val:[];set(checked?c.filter(v=>v!==o):[...c,o])}} style={{accentColor:'var(--forest)',marginTop:2}}/>{o}</label>})}</div>
    return <input className="form-input" type="text" value={val} onChange={e => set(e.target.value)} />
  }
  return (
    <div style={{marginTop:24}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,padding:'12px 16px',background:'rgba(30,61,48,0.05)',border:'1.5px solid var(--forest-border)',borderRadius:12}}>
        <span style={{fontSize:20}}>{questionnaire.icon}</span>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:'var(--forest)'}}>{questionnaire.label} — Claims Questionnaire</div>
          <div style={{fontSize:12,color:'var(--muted)',marginTop:1}}>Complete all applicable sections. Answers save to the claim record.</div>
        </div>
      </div>
      {questionnaire.sections.map((section,si)=>(
        <div key={si} style={{marginBottom:16,background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
          <div style={{padding:'10px 16px',background:'var(--forest)',color:'#fff',fontSize:12,fontWeight:700,letterSpacing:'.4px',textTransform:'uppercase'}}>{section.title}</div>
          <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>
            {section.fields.map(field=>(
              <div key={field.key}>
                <label className="form-label" style={{marginBottom:5}}>{field.label}</label>
                {renderField(field)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── CLAIM DOCUMENTS SECTION ──────────────────────────────────────────────────
const DOC_CATEGORIES = [
  'Claim Form', 'Photos / Evidence', 'Insurer Correspondence', 'Email',
  'Policy Schedule', 'Repair Quote', 'Invoice', 'Police Report',
  'Third Party Correspondence', 'Settlement', 'Other',
]

const DOC_CAT_COLORS = {
  'Email': { bg: 'rgba(59,130,246,0.1)', color: '#2563eb' },
  'Photos / Evidence': { bg: 'rgba(139,92,246,0.1)', color: '#7c3aed' },
  'Insurer Correspondence': { bg: 'rgba(230,168,23,0.12)', color: '#7a5500' },
  'Third Party Correspondence': { bg: 'rgba(220,53,69,0.08)', color: '#dc3545' },
  'Settlement': { bg: 'rgba(30,158,117,0.1)', color: '#1e9e75' },
  'Claim Form': { bg: 'rgba(30,61,48,0.08)', color: '#1e3d30' },
  'Repair Quote': { bg: 'rgba(59,130,246,0.08)', color: '#2563eb' },
  'Invoice': { bg: 'rgba(139,92,246,0.08)', color: '#7c3aed' },
  'Police Report': { bg: 'rgba(220,53,69,0.06)', color: '#dc3545' },
}

function ClaimDocuments({ claimId, showToast, user }) {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [category, setCategory] = useState('Other')
  const [docNote, setDocNote] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('claim_documents')
      .select('*')
      .eq('claim_id', claimId)
      .order('created_at', { ascending: false })
    setDocs(data || [])
    setLoading(false)
  }, [claimId])

  useEffect(() => { load() }, [load])

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return
    setUploading(true)
    let count = 0
    for (const file of Array.from(files)) {
      if (file.size > 20 * 1024 * 1024) { showToast(`${file.name} exceeds 20MB limit`); continue }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const path = `${claimId}/${Date.now()}_${safeName}`
      const { error: upErr } = await supabase.storage.from('claim-documents').upload(path, file)
      if (upErr) { showToast(`Upload failed: ${upErr.message}`); continue }
      const { error: dbErr } = await supabase.from('claim_documents').insert({
        claim_id: claimId, file_name: file.name, file_path: path,
        file_size: file.size, file_type: file.type || 'application/octet-stream',
        category, notes: docNote || null, uploaded_by: user?.name || 'Broker',
      })
      if (dbErr) { showToast(`Save failed: ${dbErr.message}`); continue }
      count++
    }
    if (count > 0) { showToast(`${count} file${count > 1 ? 's' : ''} uploaded`); setDocNote(''); await load() }
    setUploading(false)
  }

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files) }
  const handleFileInput = (e) => { uploadFiles(e.target.files); e.target.value = '' }

  const downloadDoc = async (doc) => {
    const { data, error } = await supabase.storage.from('claim-documents').createSignedUrl(doc.file_path, 300)
    if (error || !data?.signedUrl) return showToast('Download failed')
    window.open(data.signedUrl, '_blank')
  }

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return
    await supabase.storage.from('claim-documents').remove([doc.file_path])
    await supabase.from('claim_documents').delete().eq('id', doc.id)
    showToast('Document deleted')
    await load()
  }

  const fmtSize = (bytes) => {
    if (!bytes) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  const fileId = `claim-doc-input-${claimId}`

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)', marginBottom: 10 }}>
        📎 Documents & Emails ({docs.length})
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}
          style={{ flex: '0 0 auto', width: 'auto', fontSize: 12, padding: '5px 10px' }}>
          {DOC_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <input className="form-input" value={docNote} onChange={e => setDocNote(e.target.value)}
          placeholder="Optional note (e.g. email subject, quote ref)"
          style={{ flex: 1, fontSize: 12, padding: '5px 10px' }} />
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => document.getElementById(fileId).click()}
        style={{
          border: `1.5px dashed ${dragOver ? 'var(--forest)' : 'var(--border)'}`,
          borderRadius: 10, padding: '14px 16px', textAlign: 'center', marginBottom: 12,
          background: dragOver ? 'rgba(30,61,48,0.04)' : 'var(--surface2)',
          cursor: 'pointer', transition: 'all .15s',
        }}
      >
        <input id={fileId} type="file" multiple style={{ display: 'none' }} onChange={handleFileInput} />
        {uploading
          ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>Uploading...</div>
          : <div style={{ fontSize: 12, color: 'var(--muted)' }}>Drop files here or click to browse · PDFs, images, emails · 20MB max</div>
        }
      </div>

      {loading
        ? <div style={{ fontSize: 12, color: 'var(--muted)', padding: 8 }}>Loading...</div>
        : docs.length === 0
          ? <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', padding: '8px 0' }}>No documents uploaded yet</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {docs.map(doc => {
                const cc = DOC_CAT_COLORS[doc.category] || { bg: 'var(--surface2)', color: 'var(--muted)' }
                const isImage = doc.file_type?.startsWith('image/')
                return (
                  <div key={doc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8,
                  }}>
                    <div style={{ fontSize: 18, flexShrink: 0 }}>
                      {isImage ? '🖼' : doc.file_type?.includes('pdf') ? '📄' : doc.category === 'Email' ? '📧' : '📎'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 8, background: cc.bg, color: cc.color }}>{doc.category}</span>
                        <span style={{ fontSize: 10, color: 'var(--muted)' }}>{fmtSize(doc.file_size)}</span>
                        <span style={{ fontSize: 10, color: 'var(--muted)' }}>{doc.uploaded_by}</span>
                        <span style={{ fontSize: 10, color: 'var(--muted)' }}>{fmtDate(doc.created_at)}</span>
                      </div>
                      {doc.notes && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{doc.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => downloadDoc(doc)}>⬇</button>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px', color: 'var(--danger)' }} onClick={() => deleteDoc(doc)}>✕</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )
      }
    </div>
  )
}

function LodgeTab({ showToast, user, onClaimSaved, clients, focusClaim, clearFocus }) {
  const [showForm, setShowForm] = useState(false)
  const [showClosed, setShowClosed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [openClaims, setOpenClaims] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedClaim, setSelectedClaim] = useState(null)
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [savingEdit, setSavingEdit] = useState(false)
  const [questionnaire, setQuestionnaire] = useState(null)
  const [claimData, setClaimData] = useState({})
  const [savedClaimId, setSavedClaimId] = useState(null)

  const [form, setForm] = useState({
    client_name: '',
    client_id: '',
    cover_type: '',
    policy_number: '',
    insurer: '',
    date_of_loss: '',
    description: '',
    estimated_loss: '',
    assigned_broker: user?.name || 'Mitch Speer',
    urgency: 'Standard',
    is_claims_made: false,
  })

  const load = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('claims').select('*')
    if (!showClosed) query = query.not('status', 'in', '("Settled","Withdrawn")')
    const { data } = await query.order('created_at', { ascending: false })
    setOpenClaims(data || [])
    setLoading(false)
  }, [showClosed])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (focusClaim) {
      setShowClosed(true)
      setSelectedClaim(focusClaim)
      setEditing(false)
      if (clearFocus) clearFocus()
    }
  }, [focusClaim, clearFocus])

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }))

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId)
    if (client) {
      update('client_id', client.id)
      update('client_name', client.company_name)
      update('insurer', client.policy_type ? '' : '')
      update('cover_type', client.policy_type || '')
      if (isClaimsMadeType(client.policy_type || '')) update('is_claims_made', true)
    }
  }

  const saveClaim = async () => {
    if (!form.client_name) return showToast('Client name is required')
    if (!form.cover_type) return showToast('Cover type is required')
    if (!form.date_of_loss) return showToast('Date of loss is required')
    if (!form.description) return showToast('Description is required')

    setSaving(true)
    const payload = {
      client_id: form.client_id || null,
      client_name: form.client_name,
      assigned_broker: form.assigned_broker,
      cover_type: form.cover_type,
      policy_number: form.policy_number || null,
      insurer: form.insurer || null,
      date_of_loss: form.date_of_loss,
      date_lodged: new Date().toISOString().split('T')[0],
      description: form.description,
      estimated_loss: form.estimated_loss ? Number(form.estimated_loss) : null,
      status: 'Lodged',
      is_claims_made: form.is_claims_made,
      urgency: form.urgency,
      broker_notes: [{
        text: `Claim lodged by ${user?.name || 'Broker'}. ${form.description}`,
        broker: user?.name || 'Broker',
        timestamp: new Date().toISOString(),
        type: 'lodged',
      }],
      last_updated_at: new Date().toISOString(),
    }

    const { data: inserted, error } = await supabase.from('claims').insert(payload).select().single()
    if (error) { showToast('Error saving claim: ' + error.message); setSaving(false); return }

    setSavedClaimId(inserted.id)
    setQuestionnaire(getQuestionnaireForCoverType(form.cover_type))
    setClaimData({ insured_name: form.client_name, policy_number: form.policy_number, incident_date: form.date_of_loss })
    await load()
    if (onClaimSaved) onClaimSaved()
    showToast('✅ Claim lodged — complete the questionnaire below')
    setSaving(false)
  }

  const addNote = async () => {
    if (!newNote.trim() || !selectedClaim) return
    setAddingNote(true)
    const updatedNotes = [
      ...(selectedClaim.broker_notes || []),
      { text: newNote.trim(), broker: user?.name || 'Broker', timestamp: new Date().toISOString(), type: 'note' },
    ]
    await supabase.from('claims').update({
      broker_notes: updatedNotes,
      last_updated_at: new Date().toISOString(),
    }).eq('id', selectedClaim.id)
    setSelectedClaim(prev => ({ ...prev, broker_notes: updatedNotes, last_updated_at: new Date().toISOString() }))
    setOpenClaims(prev => prev.map(c => c.id === selectedClaim.id ? { ...c, broker_notes: updatedNotes } : c))
    setNewNote('')
    showToast('Note added')
    setAddingNote(false)
  }

  const updateStatus = async (claimId, newStatus) => {
    setUpdatingStatus(true)
    const claim = openClaims.find(c => c.id === claimId)
    const statusNote = {
      text: `Status updated to "${newStatus}" by ${user?.name || 'Broker'}`,
      broker: user?.name || 'Broker',
      timestamp: new Date().toISOString(),
      type: 'status',
    }
    const updatedNotes = [...(claim?.broker_notes || []), statusNote]
    await supabase.from('claims').update({
      status: newStatus,
      broker_notes: updatedNotes,
      last_updated_at: new Date().toISOString(),
    }).eq('id', claimId)
    if (selectedClaim?.id === claimId) {
      setSelectedClaim(prev => ({ ...prev, status: newStatus, broker_notes: updatedNotes }))
    }
    await load()
    if (onClaimSaved) onClaimSaved()
    showToast(`Status → ${newStatus}`)
    setUpdatingStatus(false)
  }

  const startEdit = () => {
    setEditForm({
      client_name: selectedClaim.client_name || '',
      cover_type: selectedClaim.cover_type || '',
      policy_number: selectedClaim.policy_number || '',
      insurer: selectedClaim.insurer || '',
      date_of_loss: (selectedClaim.date_of_loss || '').slice(0, 10),
      estimated_loss: selectedClaim.estimated_loss ?? '',
      assigned_broker: selectedClaim.assigned_broker || 'Mitch Speer',
      urgency: selectedClaim.urgency || 'Standard',
      is_claims_made: !!selectedClaim.is_claims_made,
      description: selectedClaim.description || '',
    })
    setEditing(true)
  }

  const saveEdit = async () => {
    if (!editForm.client_name) return showToast('Client name is required')
    if (!editForm.cover_type) return showToast('Cover type is required')
    if (!editForm.date_of_loss) return showToast('Date of loss is required')
    if (!editForm.description) return showToast('Description is required')
    setSavingEdit(true)
    const editNote = {
      text: `Claim details edited by ${user?.name || 'Broker'}`,
      broker: user?.name || 'Broker', timestamp: new Date().toISOString(), type: 'edit',
    }
    const updates = {
      client_name: editForm.client_name,
      cover_type: editForm.cover_type,
      policy_number: editForm.policy_number || null,
      insurer: editForm.insurer || null,
      date_of_loss: editForm.date_of_loss,
      estimated_loss: editForm.estimated_loss !== '' ? Number(editForm.estimated_loss) : null,
      assigned_broker: editForm.assigned_broker,
      urgency: editForm.urgency,
      is_claims_made: editForm.is_claims_made,
      description: editForm.description,
      broker_notes: [...(selectedClaim.broker_notes || []), editNote],
      last_updated_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('claims').update(updates).eq('id', selectedClaim.id)
    if (error) { showToast('Error saving: ' + error.message); setSavingEdit(false); return }
    setSelectedClaim(prev => ({ ...prev, ...updates }))
    setOpenClaims(prev => prev.map(c => c.id === selectedClaim.id ? { ...c, ...updates } : c))
    setEditing(false)
    if (onClaimSaved) onClaimSaved()
    showToast('Claim updated')
    setSavingEdit(false)
  }

  const updateClaimRef = async (claimId, ref) => {
    await supabase.from('claims').update({ claim_reference: ref, last_updated_at: new Date().toISOString() }).eq('id', claimId)
    setOpenClaims(prev => prev.map(c => c.id === claimId ? { ...c, claim_reference: ref } : c))
    if (selectedClaim?.id === claimId) setSelectedClaim(prev => ({ ...prev, claim_reference: ref }))
    showToast('Claim reference saved')
  }

  const saveQuestionnaire = async () => {
    if (!savedClaimId) return
    setSaving(true)
    await supabase.from('claims').update({ claim_data: claimData, last_updated_at: new Date().toISOString() }).eq('id', savedClaimId)
    await load()
    setShowForm(false); setQuestionnaire(null); setClaimData({}); setSavedClaimId(null)
    setForm({ client_name:'', client_id:'', cover_type:'', policy_number:'', insurer:'', date_of_loss:'', description:'', estimated_loss:'', assigned_broker: user?.name||'Mitch Speer', urgency:'Standard', is_claims_made:false })
    showToast('📋 Questionnaire saved')
    setSaving(false)
  }

  const daysSinceUpdate = (claim) => {
    if (!claim.last_updated_at) return null
    return Math.floor((new Date() - new Date(claim.last_updated_at)) / 86400000)
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{showClosed ? 'All Claims' : 'Open Claims'} ({openClaims.length})</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{showClosed ? 'Including settled & withdrawn — reopen or add notes as needed' : 'Active claims requiring broker action'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowClosed(s => !s)}>
            {showClosed ? 'Hide closed' : 'Show closed'}
          </button>
          <button className="btn btn-orange btn-sm" onClick={() => setShowForm(s => !s)}>
            {showForm ? '✕ Cancel' : '+ Lodge New Claim'}
          </button>
        </div>
      </div>

      {/* Lodge form */}
      {showForm && (
        <div style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)', marginBottom: 16 }}>📋 New Claim</div>

          {/* Claims-made warning */}
          {form.is_claims_made && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(220,53,69,0.08)', border: '1.5px solid rgba(220,53,69,0.3)', borderRadius: 10, fontSize: 13, color: '#dc3545', fontWeight: 600 }}>
              ⚠️ Claims-made policy — this circumstance must be notified to the insurer immediately during the current period of insurance.
            </div>
          )}

          <div className="form-grid">
            {/* Client selector */}
            <div>
              <label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id || ''} onChange={e => handleClientSelect(e.target.value)}>
                <option value="">Select client...</option>
                {(clients || []).map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Assigned Broker</label>
              <select className="form-select" value={form.assigned_broker} onChange={e => update('assigned_broker', e.target.value)}>
                {BROKERS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Cover Type *</label>
              <select className="form-select" value={form.cover_type} onChange={e => {
                update('cover_type', e.target.value)
                update('is_claims_made', isClaimsMadeType(e.target.value))
              }}>
                <option value="">Select...</option>
                {COVER_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Policy Number</label>
              <input className="form-input" value={form.policy_number} onChange={e => update('policy_number', e.target.value)} placeholder="e.g. QBE-2026-001" />
            </div>
            <div>
              <label className="form-label">Insurer</label>
              <input className="form-input" value={form.insurer} onChange={e => update('insurer', e.target.value)} placeholder="e.g. QBE, Allianz" />
            </div>
            <div>
              <label className="form-label">Date of Loss *</label>
              <input className="form-input" type="date" value={form.date_of_loss} onChange={e => update('date_of_loss', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Estimated Loss ($)</label>
              <input className="form-input" type="number" value={form.estimated_loss} onChange={e => update('estimated_loss', e.target.value)} placeholder="e.g. 15000" />
            </div>
            <div>
              <label className="form-label">Urgency</label>
              <select className="form-select" value={form.urgency} onChange={e => update('urgency', e.target.value)}>
                <option>Standard</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
            </div>
            <div className="full">
              <label className="form-label">Description of Incident *</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => update('description', e.target.value)}
                placeholder="Describe what happened, when, where, and what was damaged or lost. Be specific." />
            </div>
            <div className="full" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="claims-made-check" checked={form.is_claims_made}
                onChange={e => update('is_claims_made', e.target.checked)}
                style={{ accentColor: 'var(--forest)', width: 16, height: 16 }} />
              <label htmlFor="claims-made-check" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                This is a claims-made policy — immediate insurer notification required
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-orange" onClick={saveClaim} disabled={saving || !!questionnaire}>{saving ? 'Lodging...' : 'Lodge Claim →'}</button>
          </div>

          {questionnaire && (
            <>
              <QuestionnaireRenderer questionnaire={questionnaire} claimData={claimData} onChange={setClaimData} />
              <div className="form-actions" style={{marginTop:16}}>
                <button className="btn btn-secondary" onClick={()=>{setQuestionnaire(null);setClaimData({});setSavedClaimId(null);setShowForm(false);setForm({client_name:'',client_id:'',cover_type:'',policy_number:'',insurer:'',date_of_loss:'',description:'',estimated_loss:'',assigned_broker:user?.name||'Mitch Speer',urgency:'Standard',is_claims_made:false})}}>Skip</button>
                <button className="btn btn-primary" onClick={saveQuestionnaire} disabled={saving}>{saving?'Saving...':'💾 Save Questionnaire'}</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Claims list + detail panel */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedClaim ? '1fr 1.2fr' : '1fr', gap: 16 }}>
        {/* List */}
        <div className="card" style={{ height: 'fit-content' }}>
          {loading
            ? <div className="loading"><div className="spinner" /><span>Loading...</span></div>
            : openClaims.length === 0
              ? <div className="empty" style={{ padding: '32px 24px' }}>
                  <div className="empty-icon">📋</div>
                  <div className="empty-title">No open claims</div>
                  <div className="empty-sub">Lodge a claim above to get started</div>
                </div>
              : openClaims.map(claim => {
                  const daysSinceUpd = daysSinceUpdate(claim)
                  const overdue = daysSinceUpd !== null && daysSinceUpd >= 10
                  const urgStyle = URGENCY_COLORS[claim.urgency] || URGENCY_COLORS.Standard
                  const isSelected = selectedClaim?.id === claim.id
                  return (
                    <div key={claim.id}
                      onClick={() => { setEditing(false); setSelectedClaim(isSelected ? null : claim) }}
                      style={{
                        padding: '14px 20px', borderBottom: '1.5px solid var(--border)',
                        cursor: 'pointer', transition: 'background .15s',
                        background: isSelected ? 'var(--forest-subtle)' : 'var(--surface)',
                        borderLeft: `3px solid ${overdue ? '#dc3545' : claim.urgency === 'Urgent' ? '#dc3545' : claim.urgency === 'High' ? '#e6a817' : 'var(--forest)'}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{claim.client_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                            {claim.cover_type}{claim.insurer ? ` · ${claim.insurer}` : ''}{claim.policy_number ? ` · ${claim.policy_number}` : ''}
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            <StatusBadge status={claim.status} />
                            {claim.urgency !== 'Standard' && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: urgStyle.bg, color: urgStyle.color }}>
                                {claim.urgency}
                              </span>
                            )}
                            {claim.is_claims_made && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(220,53,69,0.1)', color: '#dc3545' }}>
                                Claims-made
                              </span>
                            )}
                            {overdue && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(220,53,69,0.1)', color: '#dc3545' }}>
                                ⚠️ {daysSinceUpd}d since update
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Lodged {fmtDate(claim.date_lodged)}</div>
                          {claim.estimated_loss && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--forest)', marginTop: 2 }}>{fmtCurr(claim.estimated_loss)}</div>}
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{claim.assigned_broker?.split(' ')[0]}</div>
                        </div>
                      </div>
                    </div>
                  )
                })
          }
        </div>

        {/* Detail panel */}
        {selectedClaim && (
          <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden', height: 'fit-content' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--forest-subtle)' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{selectedClaim.client_name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{selectedClaim.cover_type} · {selectedClaim.insurer || 'Insurer TBC'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!editing && <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: '4px 10px' }} onClick={startEdit}>✎ Edit</button>}
                <button onClick={() => { setEditing(false); setSelectedClaim(null) }} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
            </div>

            <div style={{ padding: '16px 20px' }}>
              {/* Claims-made warning */}
              {selectedClaim.is_claims_made && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(220,53,69,0.08)', border: '1.5px solid rgba(220,53,69,0.25)', borderRadius: 10, fontSize: 12, color: '#dc3545', fontWeight: 600 }}>
                  ⚠️ Claims-made policy — insurer notification required during current period
                </div>
              )}

              {editing ? (
                <div style={{ marginBottom: 16 }}>
                  <div className="form-grid">
                    <div><label className="form-label">Client *</label>
                      <input className="form-input" value={editForm.client_name} onChange={e => setEditForm(p => ({ ...p, client_name: e.target.value }))} /></div>
                    <div><label className="form-label">Assigned Broker</label>
                      <select className="form-select" value={editForm.assigned_broker} onChange={e => setEditForm(p => ({ ...p, assigned_broker: e.target.value }))}>{BROKERS.map(b => <option key={b}>{b}</option>)}</select></div>
                    <div><label className="form-label">Cover Type *</label>
                      <select className="form-select" value={editForm.cover_type} onChange={e => setEditForm(p => ({ ...p, cover_type: e.target.value }))}><option value="">Select...</option>{COVER_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
                    <div><label className="form-label">Policy Number</label>
                      <input className="form-input" value={editForm.policy_number} onChange={e => setEditForm(p => ({ ...p, policy_number: e.target.value }))} /></div>
                    <div><label className="form-label">Insurer</label>
                      <input className="form-input" value={editForm.insurer} onChange={e => setEditForm(p => ({ ...p, insurer: e.target.value }))} /></div>
                    <div><label className="form-label">Date of Loss *</label>
                      <input className="form-input" type="date" value={editForm.date_of_loss} onChange={e => setEditForm(p => ({ ...p, date_of_loss: e.target.value }))} /></div>
                    <div><label className="form-label">Estimated Loss ($)</label>
                      <input className="form-input" type="number" value={editForm.estimated_loss} onChange={e => setEditForm(p => ({ ...p, estimated_loss: e.target.value }))} /></div>
                    <div><label className="form-label">Urgency</label>
                      <select className="form-select" value={editForm.urgency} onChange={e => setEditForm(p => ({ ...p, urgency: e.target.value }))}><option>Standard</option><option>High</option><option>Urgent</option></select></div>
                    <div className="full"><label className="form-label">Description *</label>
                      <textarea className="form-input" rows={3} value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} /></div>
                    <div className="full" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="checkbox" id="edit-claims-made" checked={editForm.is_claims_made} onChange={e => setEditForm(p => ({ ...p, is_claims_made: e.target.checked }))} style={{ accentColor: 'var(--forest)', width: 16, height: 16 }} />
                      <label htmlFor="edit-claims-made" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Claims-made policy — immediate insurer notification required</label></div>
                  </div>
                  <div className="form-actions" style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                    <button className="btn btn-orange" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </div>
              ) : (<>
              {/* Key details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[
                  ['Date of Loss', fmtDate(selectedClaim.date_of_loss)],
                  ['Date Lodged', fmtDate(selectedClaim.date_lodged)],
                  ['Policy Number', selectedClaim.policy_number || '—'],
                  ['Estimated Loss', fmtCurr(selectedClaim.estimated_loss)],
                  ['Assigned Broker', selectedClaim.assigned_broker || '—'],
                  ['Urgency', selectedClaim.urgency || 'Standard'],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Claim reference */}
              <div style={{ marginBottom: 16 }}>
                <label className="form-label">Insurer Claim Reference</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" defaultValue={selectedClaim.claim_reference || ''}
                    placeholder="e.g. CLM-2026-001234"
                    onBlur={e => { if (e.target.value !== (selectedClaim.claim_reference || '')) updateClaimRef(selectedClaim.id, e.target.value) }}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.7 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>Incident Description</div>
                {selectedClaim.description}
              </div>
              </>)}

              {/* Status update */}
              <div style={{ marginBottom: 16 }}>
                <label className="form-label">Update Status</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CLAIM_STATUSES.map(s => {
                    const sc = CLAIM_STATUS_COLORS[s] || {}
                    const isActive = selectedClaim.status === s
                    return (
                      <button key={s} disabled={updatingStatus || isActive}
                        onClick={() => updateStatus(selectedClaim.id, s)}
                        style={{
                          padding: '5px 12px', borderRadius: 20, border: '1.5px solid',
                          fontSize: 11, fontWeight: 600, cursor: isActive ? 'default' : 'pointer',
                          fontFamily: 'var(--font)', transition: 'all .15s',
                          background: isActive ? sc.color : 'var(--surface2)',
                          borderColor: isActive ? sc.color : 'var(--border)',
                          color: isActive ? '#fff' : 'var(--muted)',
                          opacity: updatingStatus ? 0.6 : 1,
                        }}
                      >{s}</button>
                    )
                  })}
                </div>
              </div>

              {/* Declined escalation guide */}
              {selectedClaim.status === 'Declined' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(220,53,69,0.06)', border: '1.5px solid rgba(220,53,69,0.2)', borderRadius: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#dc3545', marginBottom: 8 }}>⚠️ Declined Claim — Escalation Steps</div>
                  {[
                    '1. Request written reasons from the insurer',
                    '2. Review the policy wording against the reason for decline',
                    '3. Lodge internal review with McLardy McShane — tracy@mclardymcshane.com.au',
                    '4. Steadfast Customer Advocacy — customeradvocacy@steadfast.com.au',
                    '5. AFCA — 1800 931 678 (free, binding on insurer)',
                  ].map(step => (
                    <div key={step} style={{ fontSize: 11, color: 'var(--text)', padding: '2px 0', lineHeight: 1.6 }}>{step}</div>
                  ))}
                </div>
              )}

              {/* Broker notes */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)', marginBottom: 10 }}>Broker Notes</div>
                {(selectedClaim.broker_notes || []).length === 0
                  ? <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>No notes yet</div>
                  : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
                      {[...(selectedClaim.broker_notes || [])].reverse().map((note, i) => (
                        <div key={i} style={{
                          padding: '8px 12px', borderRadius: 8,
                          background: note.type === 'status' ? 'rgba(30,61,48,0.06)' : note.type === 'lodged' ? 'rgba(59,130,246,0.06)' : 'var(--surface2)',
                          border: '1px solid var(--border)',
                        }}>
                          <div style={{ fontSize: 12, lineHeight: 1.6 }}>{note.text}</div>
                          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>
                            {note.broker} · {note.timestamp ? new Date(note.timestamp).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                }
                <textarea className="form-input" rows={2} placeholder="Add a broker note..." value={newNote} onChange={e => setNewNote(e.target.value)} style={{ marginBottom: 8 }} />
                <button className="btn btn-primary btn-sm" onClick={addNote} disabled={addingNote || !newNote.trim()}>
                  {addingNote ? 'Adding...' : 'Add Note'}
                </button>
              </div>

              {/* Documents */}
              <div style={{ borderTop: '1.5px solid var(--border)', paddingTop: 14 }}>
                <ClaimDocuments claimId={selectedClaim.id} showToast={showToast} user={user} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── TRACK & ADVOCATE TAB ─────────────────────────────────────────────────────
function TrackTab({ showToast, user, onManage }) {
  const [claims, setClaims] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [brokerFilter, setBrokerFilter] = useState('all')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('claims')
      .select('*')
      .order('last_updated_at', { ascending: false })
    setClaims(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = claims.filter(c => {
    const statusMatch = statusFilter === 'all' || c.status === statusFilter
    const brokerMatch = brokerFilter === 'all' || c.assigned_broker === brokerFilter
    return statusMatch && brokerMatch
  })

  const updateStatus = async (claimId, newStatus, settledAmount = null) => {
    const updates = {
      status: newStatus,
      last_updated_at: new Date().toISOString(),
    }
    if (settledAmount) updates.settled_amount = settledAmount
    if (newStatus === 'Settled' || newStatus === 'Declined') updates.outcome = newStatus

    const claim = claims.find(c => c.id === claimId)
    const statusNote = {
      text: `Status updated to "${newStatus}" by ${user?.name || 'Broker'}`,
      broker: user?.name || 'Broker',
      timestamp: new Date().toISOString(),
      type: 'status',
    }
    updates.broker_notes = [...(claim?.broker_notes || []), statusNote]

    await supabase.from('claims').update(updates).eq('id', claimId)
    await load()
    showToast(`Status → ${newStatus}`)
  }

  const openCount = claims.filter(c => !['Settled', 'Withdrawn', 'Declined'].includes(c.status)).length
  const overdueCount = claims.filter(c => {
    const days = Math.floor((new Date() - new Date(c.last_updated_at)) / 86400000)
    return days >= 10 && !['Settled', 'Withdrawn', 'Declined'].includes(c.status)
  }).length

  return (
    <div>
      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Open Claims', value: openCount, color: 'var(--info)' },
          { label: 'Overdue Updates', value: overdueCount, color: overdueCount > 0 ? 'var(--danger)' : 'var(--forest)' },
          { label: 'Settled (All Time)', value: claims.filter(c => c.status === 'Settled').length, color: 'var(--forest)' },
          { label: 'Declined (All Time)', value: claims.filter(c => c.status === 'Declined').length, color: 'var(--danger)' },
        ].map(item => (
          <div key={item.label} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="form-select" style={{ width: 'auto', fontSize: 13 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {CLAIM_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="form-select" style={{ width: 'auto', fontSize: 13 }} value={brokerFilter} onChange={e => setBrokerFilter(e.target.value)}>
          <option value="all">All Brokers</option>
          {BROKERS.map(b => <option key={b}>{b}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>{filtered.length} claims</span>
      </div>

      {loading
        ? <div className="loading"><div className="spinner" /><span>Loading...</span></div>
        : filtered.length === 0
          ? <div className="empty" style={{ padding: '32px 24px' }}>
              <div className="empty-icon">📋</div>
              <div className="empty-title">No claims found</div>
            </div>
          : (
            <div className="card">
              <div className="tbl-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 120px' }}>
                <span>Client</span><span>Cover</span><span>Status</span><span>Lodged</span><span>Last Update</span><span>Broker</span><span>Actions</span>
              </div>
              {filtered.map(claim => {
                const daysSinceUpd = daysSince(claim.last_updated_at)
                const overdue = daysSinceUpd !== null && daysSinceUpd >= 10 && !['Settled', 'Withdrawn', 'Declined'].includes(claim.status)
                return (
                  <div key={claim.id} className="tbl-row" onClick={() => onManage && onManage(claim)} style={{
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 120px',
                    borderLeft: `3px solid ${overdue ? '#dc3545' : 'var(--forest)'}`,
                    cursor: 'pointer',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{claim.client_name}</div>
                      {claim.claim_reference && <div style={{ fontSize: 11, color: 'var(--muted)' }}>Ref: {claim.claim_reference}</div>}
                      {claim.is_claims_made && <span style={{ fontSize: 10, fontWeight: 700, color: '#dc3545' }}>Claims-made</span>}
                    </div>
                    <div style={{ fontSize: 12 }}>{claim.cover_type}</div>
                    <div><StatusBadge status={claim.status} /></div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtDate(claim.date_lodged)}</div>
                    <div>
                      <div style={{ fontSize: 12, color: overdue ? '#dc3545' : 'var(--muted)', fontWeight: overdue ? 700 : 400 }}>
                        {daysSinceUpd === 0 ? 'Today' : `${daysSinceUpd}d ago`}
                      </div>
                      {overdue && <div style={{ fontSize: 10, color: '#dc3545', fontWeight: 700 }}>Update overdue</div>}
                    </div>
                    <div style={{ fontSize: 12 }}>{claim.assigned_broker?.split(' ')[0]}</div>
                    <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                      {claim.status !== 'Settled' && claim.status !== 'Withdrawn' && (
                        <>
                          {claim.status !== 'Declined' && (
                            <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}
                              onClick={() => {
                                const next = CLAIM_STATUSES[Math.min(CLAIM_STATUSES.indexOf(claim.status) + 1, CLAIM_STATUSES.length - 1)]
                                updateStatus(claim.id, next)
                              }}>
                              → Next
                            </button>
                          )}
                          <button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}
                            onClick={() => updateStatus(claim.id, 'Declined')}>
                            Decline
                          </button>
                        </>
                      )}
                      {claim.status === 'Settlement Offered' && (
                        <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}
                          onClick={() => {
                            const amt = window.prompt('Settled amount ($)?')
                            if (amt) updateStatus(claim.id, 'Settled', Number(amt))
                          }}>
                          Settle
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )
      }
    </div>
  )
}

// ─── REGISTER & REPORTING TAB ─────────────────────────────────────────────────
function RegisterTab({ showToast }) {
  const [claims, setClaims] = useState([])
  const [loading, setLoading] = useState(true)
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()))

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('claims')
      .select('*')
      .order('created_at', { ascending: false })
    setClaims(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const years = [...new Set(claims.map(c => c.created_at?.slice(0, 4)).filter(Boolean))].sort().reverse()
  const filtered = claims.filter(c => !yearFilter || c.created_at?.startsWith(yearFilter))

  const settled = filtered.filter(c => c.status === 'Settled')
  const declined = filtered.filter(c => c.status === 'Declined')
  const open = filtered.filter(c => !['Settled', 'Declined', 'Withdrawn'].includes(c.status))
  const totalSettled = settled.reduce((sum, c) => sum + (c.settled_amount || 0), 0)
  const totalEstimated = filtered.reduce((sum, c) => sum + (c.estimated_loss || 0), 0)

  const exportCsv = () => {
    const rows = [
      ['Client', 'Cover Type', 'Insurer', 'Policy Number', 'Date of Loss', 'Date Lodged', 'Status', 'Claim Reference', 'Estimated Loss', 'Settled Amount', 'Broker', 'Urgency', 'Claims-Made'],
      ...filtered.map(c => [
        c.client_name, c.cover_type, c.insurer || '', c.policy_number || '',
        c.date_of_loss || '', c.date_lodged || '', c.status, c.claim_reference || '',
        c.estimated_loss || '', c.settled_amount || '', c.assigned_broker || '',
        c.urgency || 'Standard', c.is_claims_made ? 'Yes' : 'No',
      ]),
    ]
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `benchmark-claims-${yearFilter || 'all'}.csv`
    a.click()
    URL.revokeObjectURL(url)
    showToast('Claims register exported')
  }

  return (
    <div>
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Claims', value: filtered.length, color: 'var(--text)' },
          { label: 'Open', value: open.length, color: 'var(--info)' },
          { label: 'Settled', value: settled.length, sub: totalSettled ? fmtCurr(totalSettled) : null, color: 'var(--forest)' },
          { label: 'Declined', value: declined.length, color: declined.length > 0 ? 'var(--danger)' : 'var(--muted)' },
        ].map(item => (
          <div key={item.label} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: item.color }}>{item.value}</div>
            {item.sub && <div style={{ fontSize: 11, color: 'var(--forest)', fontWeight: 600, marginTop: 2 }}>{item.sub} settled</div>}
          </div>
        ))}
      </div>

      {totalEstimated > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, display: 'flex', gap: 20, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Total Estimated Loss</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtCurr(totalEstimated)}</div>
          </div>
          {totalSettled > 0 && (
            <>
              <div style={{ width: 1, height: 32, background: 'var(--border)' }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Total Settled</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurr(totalSettled)}</div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <select className="form-select" style={{ width: 'auto', fontSize: 13 }} value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
          <option value="">All Years</option>
          {years.map(y => <option key={y}>{y}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={exportCsv}>⬇ Export CSV</button>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{filtered.length} records</span>
      </div>

      {loading
        ? <div className="loading"><div className="spinner" /><span>Loading...</span></div>
        : filtered.length === 0
          ? <div className="empty" style={{ padding: '32px 24px' }}>
              <div className="empty-icon">📋</div>
              <div className="empty-title">No claims in register</div>
            </div>
          : (
            <div className="card">
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr', gap: 10, padding: '10px 16px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
                {['Client', 'Cover', 'Status', 'Insurer / Ref', 'Date of Loss', 'Estimated', 'Broker'].map(h => (
                  <span key={h} style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</span>
                ))}
              </div>
              {filtered.map(claim => (
                <div key={claim.id} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr',
                  gap: 10, padding: '12px 16px', borderBottom: '1.5px solid var(--border)',
                  alignItems: 'center', fontSize: 12,
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{claim.client_name}</div>
                    {claim.is_claims_made && <span style={{ fontSize: 10, color: '#dc3545', fontWeight: 700 }}>Claims-made</span>}
                  </div>
                  <div>{claim.cover_type}</div>
                  <div><StatusBadge status={claim.status} /></div>
                  <div>
                    <div>{claim.insurer || '—'}</div>
                    {claim.claim_reference && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{claim.claim_reference}</div>}
                  </div>
                  <div style={{ color: 'var(--muted)' }}>{fmtDate(claim.date_of_loss)}</div>
                  <div style={{ fontWeight: 600, color: claim.settled_amount ? 'var(--forest)' : 'var(--text)' }}>
                    {claim.settled_amount ? fmtCurr(claim.settled_amount) : fmtCurr(claim.estimated_loss)}
                    {claim.settled_amount && <div style={{ fontSize: 10, color: 'var(--forest)' }}>settled</div>}
                  </div>
                  <div style={{ color: 'var(--muted)' }}>{claim.assigned_broker?.split(' ')[0]}</div>
                </div>
              ))}
            </div>
          )
      }
    </div>
  )
}

// ─── MAIN CLAIMS COMPONENT ────────────────────────────────────────────────────
export function Claims({ showToast, user, clients }) {
  const [activeTab, setActiveTab] = useState('lodge')
  const [openCount, setOpenCount] = useState(0)
  const [focusClaim, setFocusClaim] = useState(null)

  const manageClaim = (claim) => { setFocusClaim(claim); setActiveTab('lodge') }

  const loadCount = useCallback(async () => {
    const { count } = await supabase
      .from('claims')
      .select('id', { count: 'exact', head: true })
      .not('status', 'in', '("Settled","Withdrawn","Declined")')
    setOpenCount(count || 0)
  }, [])

  useEffect(() => { loadCount() }, [loadCount])

  const tabs = [
    { key: 'lodge', label: `Lodge & Manage${openCount > 0 ? ` (${openCount})` : ''}`, icon: '📋' },
    { key: 'track', label: 'Track & Advocate', icon: '🔍' },
    { key: 'register', label: 'Register & Reporting', icon: '📊' },
  ]

  return (
    <div>
      <ClaimsComplianceBlurb />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1.5px solid var(--border)' }}>
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            padding: '8px 18px', background: 'none', border: 'none',
            borderBottom: `2px solid ${activeTab === key ? 'var(--danger)' : 'transparent'}`,
            fontWeight: activeTab === key ? 700 : 500,
            color: activeTab === key ? 'var(--danger)' : 'var(--muted)',
            fontSize: 13, cursor: 'pointer', marginBottom: -1.5,
            fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all .15s',
          }}>{label}</button>
        ))}
      </div>

      {activeTab === 'lodge' && (
        <LodgeTab showToast={showToast} user={user} clients={clients} onClaimSaved={loadCount}
          focusClaim={focusClaim} clearFocus={() => setFocusClaim(null)} />
      )}
      {activeTab === 'track' && (
        <TrackTab showToast={showToast} user={user} onManage={manageClaim} />
      )}
      {activeTab === 'register' && (
        <RegisterTab showToast={showToast} />
      )}
    </div>
  )
}